import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import teacherService from '../services/teacherService';
import { toast } from 'react-toastify';
import BookingModal from '../components/BookingModal';
import './TeacherProfile.css';

const TeacherProfile = () => {
  const { teacherId } = useParams();
  const { t } = useTranslation();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [teacher, setTeacher] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    loadTeacherData();
  }, [teacherId]);

  const loadTeacherData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Loading teacher with ID:', teacherId);
      
      // Öğretmen verisini yükle
      const teacherData = await teacherService.getTeacherById(teacherId);
      console.log('✅ Teacher data loaded:', teacherData);
      
      setTeacher(teacherData);
      
      // Yorumları yükle
      try {
        const reviewsData = await teacherService.getTeacherReviews(teacherId);
        console.log('✅ Reviews loaded:', reviewsData.length, 'reviews');
        setReviews(reviewsData);
      } catch (reviewError) {
        console.warn('⚠️ Could not load reviews:', reviewError);
        // Yorumlar yüklenemese bile devam et
      }
      
    } catch (error) {
      console.error('❌ Load teacher error:', error);
      setError(error.message || 'Failed to load teacher profile');
      
      // Hata detayları
      if (error.message === 'Teacher not found') {
        toast.error(`Teacher with ID ${teacherId} not found in database`);
      } else {
        toast.error('Failed to load teacher profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBookLesson = () => {
    if (!currentUser) {
      toast.info('Please login to book a lesson');
      navigate('/login');
      return;
    }

    if (userProfile?.role === 'teacher') {
      toast.warning('Teachers cannot book lessons');
      return;
    }

    setShowBookingModal(true);
  };

  const handleSendMessage = () => {
    if (!currentUser) {
      toast.info('Please login to send a message');
      navigate('/login');
      return;
    }
    navigate('/messages', { 
      state: { 
        startConversationWith: {
          id: teacher.id,
          name: `${teacher.firstName} ${teacher.lastName}`
        }
      } 
    });
  };

  // Loading durumu
  if (loading) {
    return (
      <div className="container" style={{ padding: '80px 20px', textAlign: 'center' }}>
        <div className="spinner" style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #4F46E5',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }}></div>
        <p style={{ marginTop: '20px', color: '#666' }}>Loading teacher profile...</p>
        <p style={{ fontSize: '14px', color: '#999', marginTop: '10px' }}>Teacher ID: {teacherId}</p>
      </div>
    );
  }

  // Hata durumu
  if (error || !teacher) {
    return (
      <div className="container" style={{ padding: '80px 20px', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>😕</div>
        <h2 style={{ marginBottom: '10px' }}>Teacher Not Found</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          We couldn't find a teacher with ID: <code style={{ 
            background: '#f5f5f5', 
            padding: '4px 8px', 
            borderRadius: '4px',
            fontSize: '12px'
          }}>{teacherId}</code>
        </p>
        
        <div style={{ 
          background: '#fff3cd', 
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          textAlign: 'left'
        }}>
          <h4 style={{ marginTop: 0, color: '#856404' }}>🔧 Debug Information:</h4>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', color: '#856404' }}>
            <li>Check browser console (F12) for detailed errors</li>
            <li>Verify this teacher exists in Firebase Firestore</li>
            <li>Check if teacher's "isActive" field is set to true</li>
            <li>Error: {error || 'Teacher document not found'}</li>
          </ul>
        </div>

        <button 
          onClick={() => navigate('/find-teachers')} 
          className="btn btn-primary"
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            background: '#4F46E5',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          ← Back to Find Teachers
        </button>
        
        <p style={{ marginTop: '20px', fontSize: '14px', color: '#999' }}>
          Need help? Contact support or check Firebase Console
        </p>
      </div>
    );
  }

  // Normal görünüm
  return (
    <div className="teacher-profile-page">
      <div className="container">
        {/* Header Section */}
        <div className="profile-header">
          <div className="profile-main">
            <div className="profile-avatar-large">
              {teacher.photoURL ? (
                <img src={teacher.photoURL} alt={teacher.firstName} />
              ) : (
                <div className="avatar-placeholder-large">
                  {teacher.firstName?.charAt(0)}
                </div>
              )}
            </div>

            <div className="profile-info">
              <h1>{teacher.firstName} {teacher.lastName}</h1>
              
              {teacher.subjects && teacher.subjects.length > 0 && (
                <div className="profile-subjects">
                  <span className="label">Teaches:</span>
                  {teacher.subjects.map((subject, idx) => (
                    <span key={idx} className="subject-badge">
                      {subject}
                    </span>
                  ))}
                </div>
              )}

              {teacher.languages && teacher.languages.length > 0 && (
                <div className="profile-languages">
                  <span className="label">Speaks:</span>
                  {teacher.languages.join(', ')}
                </div>
              )}

              <div className="profile-stats">
                <div className="stat-item">
                  <div className="stat-value">
                    {teacher.rating > 0 ? `⭐ ${teacher.rating.toFixed(1)}` : '⭐ New'}
                  </div>
                  <div className="stat-label">
                    {teacher.reviewCount || 0} review{teacher.reviewCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{teacher.lessonsCompleted || 0}</div>
                  <div className="stat-label">Lessons</div>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Card */}
          <div className="booking-card">
            <div className="price-section">
              <div className="trial-price">
                <span className="price-label">Trial Lesson</span>
                <span className="price">£{((teacher.hourlyRate || 0) * 0.5).toFixed(2)}</span>
              </div>
              <div className="regular-price">
                <span className="price-label">Regular Price</span>
                <span className="price">£{teacher.hourlyRate || 0}/hr</span>
              </div>
            </div>

            <button 
              onClick={handleBookLesson}
              className="btn btn-primary btn-lg btn-block"
            >
              📅 Book Trial Lesson
            </button>

            <button 
              onClick={handleSendMessage}
              className="btn btn-secondary btn-block"
            >
              💬 Send Message
            </button>

            <div className="booking-info">
              <div className="info-item">
                ⚡ Instant confirmation
              </div>
              <div className="info-item">
                🔒 Secure payment
              </div>
              <div className="info-item">
                🎥 Video lessons
              </div>
            </div>
          </div>
        </div>

        {/* Video Section */}
        {teacher.videoIntroUrl && (
          <div className="video-section">
            <h2>Video Introduction</h2>
            <div className="video-container">
              <video 
                controls 
                poster={teacher.photoURL}
                className="intro-video"
              >
                <source src={teacher.videoIntroUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}

        {/* Tabs Section */}
        <div className="profile-tabs">
          <button
            className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
          <button
            className={`tab-btn ${activeTab === 'experience' ? 'active' : ''}`}
            onClick={() => setActiveTab('experience')}
          >
            Experience
          </button>
          <button
            className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews ({teacher.reviewCount || 0})
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'about' && (
            <div className="about-section">
              <h2>About {teacher.firstName}</h2>
              {teacher.bio ? (
                <p className="bio-text">{teacher.bio}</p>
              ) : (
                <p className="text-secondary">No description provided yet.</p>
              )}
            </div>
          )}

          {activeTab === 'experience' && (
            <div className="experience-section">
              {/* Education */}
              {teacher.education && teacher.education.length > 0 && (
                <div className="credential-block">
                  <h3>🎓 Education</h3>
                  {teacher.education.map((edu, idx) => (
                    <div key={idx} className="credential-item">
                      <h4>{edu.degree} {edu.field && `in ${edu.field}`}</h4>
                      <p className="credential-subtitle">{edu.school}</p>
                      {(edu.startYear || edu.endYear) && (
                        <p className="credential-date">
                          {edu.startYear} - {edu.endYear || 'Present'}
                        </p>
                      )}
                      {edu.description && (
                        <p className="credential-description">{edu.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Experience */}
              {teacher.experience && teacher.experience.length > 0 && (
                <div className="credential-block">
                  <h3>💼 Work Experience</h3>
                  {teacher.experience.map((exp, idx) => (
                    <div key={idx} className="credential-item">
                      <h4>{exp.title}</h4>
                      <p className="credential-subtitle">{exp.company}</p>
                      {(exp.startDate || exp.endDate || exp.current) && (
                        <p className="credential-date">
                          {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                        </p>
                      )}
                      {exp.description && (
                        <p className="credential-description">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Certifications */}
              {teacher.certifications && teacher.certifications.length > 0 && (
                <div className="credential-block">
                  <h3>🏆 Certifications</h3>
                  {teacher.certifications.map((cert, idx) => (
                    <div key={idx} className="credential-item">
                      <h4>{cert.name}</h4>
                      <p className="credential-subtitle">{cert.issuer}</p>
                      {cert.date && (
                        <p className="credential-date">{cert.date}</p>
                      )}
                      {cert.credentialId && (
                        <p className="credential-id">
                          Credential ID: {cert.credentialId}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(!teacher.education || teacher.education.length === 0) &&
               (!teacher.experience || teacher.experience.length === 0) &&
               (!teacher.certifications || teacher.certifications.length === 0) && (
                <p className="text-secondary">No experience information provided yet.</p>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="reviews-section">
              {reviews.length > 0 ? (
                <>
                  <div className="reviews-summary">
                    <div className="overall-rating">
                      <div className="rating-number">
                        {teacher.rating.toFixed(1)}
                      </div>
                      <div className="rating-stars">
                        {'⭐'.repeat(Math.round(teacher.rating))}
                      </div>
                      <div className="rating-count">
                        {teacher.reviewCount} review{teacher.reviewCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  <div className="reviews-list">
                    {reviews.map((review) => (
                      <div key={review.id} className="review-item">
                        <div className="review-header">
                          <div className="reviewer-info">
                            <div className="reviewer-avatar">
                              {review.studentName?.charAt(0) || 'S'}
                            </div>
                            <div>
                              <div className="reviewer-name">
                                {review.studentName || 'Student'}
                              </div>
                              <div className="review-date">
                                {new Date(review.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div className="review-rating">
                            {'⭐'.repeat(review.rating)}
                          </div>
                        </div>
                        <p className="review-text">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="no-reviews">
                  <p>No reviews yet. Be the first to review {teacher.firstName}!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal
          teacher={teacher}
          currentUser={currentUser}
          userProfile={userProfile}
          onClose={() => setShowBookingModal(false)}
        />
      )}
    </div>
  );
};

export default TeacherProfile;